import { type SwiperType } from "@app/types"
import { getSwiperSlideById, getTileIdFromSlide, isActiveTile } from "./expanded-swiper.loader"
import { getInstance, LookupAttr } from "../extensions"
import { ISdk } from "../../"
import { playTiktokVideo, pauseTiktokVideo, resetTiktokVideo, unMuteTiktokVideo } from "./tiktok-message"

type SwiperVideoElementType = Window | HTMLElement

type VideoSource = "video" | "youtube" | "tiktok"

type SwiperVideoElementData = {
  element: SwiperVideoElementType
  source: VideoSource
}

let tiktokDefaultPlayed = false

/**
 * Play the video/audio attached to the slide on load where the element is a media element (video/youtube/tiktok)
 */
export async function playMediaOnLoad(sdk: ISdk) {
  const swiper = getInstance(sdk, `expanded`) as SwiperType
  if (swiper) {
    const activeElementData = getSwiperVideoElement(sdk, swiper, swiper.realIndex)
    triggerPlay(sdk, activeElementData)
  }
}

/**
 * Play/Pause the video/audio attached to the slide on navigation where the element is a media element (video/youtube/tiktok)
 * @param sdk
 * @param { Swiper } swiper - the swiper element
 */
export async function controlVideoPlayback(sdk: ISdk, swiper: SwiperType) {
  const activeElement = getSwiperVideoElement(sdk, swiper, swiper.realIndex)
  const previousElement = getSwiperVideoElement(sdk, swiper, swiper.previousIndex)

  if (activeElement) {
    triggerPlay(sdk, activeElement)
  }

  if (previousElement) {
    triggerPause(previousElement)
  }

  if (!activeElement && !previousElement) {
    console.warn("No active or previous video element found in controlVideoPlayback")
  }
}

/**
 * Trigger media play for different media element sources ("video", "youtube", "tiktok")
 *
 * @param sdk
 * @param elementData - the media container element and the source
 * @param elementData.element - the container element of the media (video tag or iframe.contentWindow)
 * @param elementData.source - the media source (video for custom video source, youtube/tiktok)
 */
export function triggerPlay(sdk: ISdk, elementData?: SwiperVideoElementData) {
  if (!elementData) {
    console.warn("elementData is required")
    return
  }

  const { auto_play_video = false, video_mute = false } = sdk.getExpandedTileConfig()

  switch (elementData.source) {
    case "video": {
      const videoElement = elementData.element as HTMLVideoElement
      if (auto_play_video) {
        void videoElement.play()
      }
      break
    }
    case "tiktok": {
      const tiktokFrameWindow = elementData.element as Window
      if (auto_play_video) {
        playTiktokVideo(tiktokFrameWindow)
      }
      if (!video_mute) {
        unMuteTiktokVideo(tiktokFrameWindow)
      }
      break
    }
    case "youtube": {
      const host = elementData.element as HTMLElement
      const ytPlayer = window.ugc.youtubePlayers?.[host.id]
      if (auto_play_video) {
        ytPlayer?.play()
      }
      break
    }
    default:
      throw new Error(`unsupported video source ${elementData.source}`)
  }
}
/**
 * Trigger media pause for different element sources ("video", "youtube", "tiktok")
 *
 * @param elementData - the media container element and the source
 * @param elementData.element - the container element of the media (video tag or iframe.contentWindow)
 * @param elementData.source - the media source (video for custom video source, youtube/tiktok)
 * @param reset - reset video position to 0 (default: true)
 */
export function triggerPause(elementData?: SwiperVideoElementData, reset: boolean = true) {
  if (!elementData) {
    throw new Error("elementData is required")
  }

  switch (elementData.source) {
    case "video": {
      const videoElement = elementData.element as HTMLVideoElement
      videoElement.pause()
      if (reset) {
        videoElement.currentTime = 0
      }
      break
    }
    case "tiktok": {
      const tiktokFrameWindow = elementData.element as Window
      pauseTiktokVideo(tiktokFrameWindow)
      if (reset) {
        resetTiktokVideo(tiktokFrameWindow)
      }
      break
    }
    case "youtube": {
      const host = elementData.element as HTMLElement
      const ytPlayer = window.ugc.youtubePlayers?.[host.id]
      ytPlayer?.pause()
      if (reset) {
        ytPlayer?.reset()
      }
      break
    }
    default:
      throw new Error(`unsupported video source ${elementData.source}`)
  }
}

/**
 * Get swiper video/audio element at the provided index.
 *
 * @param sdk
 * @param { Swiper } swiper - the swiper element
 * @param { number } index - index of the slide to be returned
 * @returns the video/iframe element or undefined if the element at index is not a video/audio
 */
export function getSwiperVideoElement(
  sdk: ISdk,
  swiper: SwiperType,
  index: number
): SwiperVideoElementData | undefined {
  const element = getSwiperSlideById(swiper, index)
  const tileId = getTileIdFromSlide(swiper, index)
  const youtubeId = element?.getAttribute("data-yt-id")

  if (!element) {
    throw new Error(`Failed to find element for the slide at index ${index}`)
  }

  if (!tileId) {
    throw new Error(`Failed to find tile id for the slide at index ${index}`)
  }

  const tile = sdk.getTileById(tileId)

  if (!tile) {
    throw new Error(`Failed to find tile by tile id ${tileId} in getSwiperVideoElement`)
  }

  const media = tile.media

  if (media !== "video" && media !== "short") {
    return undefined
  }

  if (youtubeId) {
    const youtubeHost = element?.querySelector<HTMLElement>(`[id="yt-frame-${tileId}-${youtubeId}"]`)
    if (youtubeHost) {
      return { element: youtubeHost, source: "youtube" }
    }
  }

  const tiktokId = element?.getAttribute("data-tiktok-id")

  if (tiktokId) {
    const tiktokFrame = element?.querySelector<HTMLIFrameElement>(`iframe#tiktok-frame-${tileId}-${tiktokId}`)
    if (tiktokFrame && tiktokFrame.contentWindow) {
      return { element: tiktokFrame.contentWindow, source: "tiktok" }
    }
  }

  const videoElement = element?.querySelector<HTMLVideoElement>(".video-content-wrapper video")

  if (videoElement) {
    return { element: videoElement, source: "video" }
  }

  console.warn("Undefined video element", element, tileId)

  return undefined
}

/**
 * Setup onload and onerror (yt-video-error) events for youtube media
 *
 * @param sdk
 * @param { Element } tile - the tile element
 * @param { HTMLElement } widgetSelector - the container of swiper element
 */
export function setupYoutubeEvents(sdk: ISdk, tile: Element, widgetSelector: HTMLElement) {
  const tileId = tile.getAttribute("data-id")
  const youtubeId = tile.getAttribute("data-yt-id")

  if (!youtubeId || !tileId) return

  // The host starts as a <div> rendered by EmbedYoutube; YT.Player replaces
  // it with an <iframe> on mount and we restore the same id on the new
  // iframe in mountYoutubePlayer. Listen at the tile level via delegation
  // so we don't need to know which element exists when, and so the synthetic
  // "load" + "yt-video-error" events (both dispatched with bubbles: true
  // by mountYoutubePlayer) reach us regardless of mount timing.
  const expectedHostId = `yt-frame-${tileId}-${youtubeId}`
  const matchesHost = (target: EventTarget | null) => target instanceof Element && target.id === expectedHostId

  tile.addEventListener("load", event => {
    if (!matchesHost(event.target)) return
    playActiveMediaTileOnLoad(sdk, tile, widgetSelector, { name: "data-yt-id", value: youtubeId })
  })
  tile.addEventListener("yt-video-error", event => {
    if (!matchesHost(event.target)) return
    const target = event.target as Element
    target.closest(".video-content-wrapper")?.classList.add("hidden")
    tile.querySelector(".video-fallback-content")?.classList.remove("hidden")
  })
}

/**
 * Setup tiktok player events using window.postMessage api
 * All media are paused by default and only the media in the active slide is played
 */
export function setupTikTokPlayerReadyEvent(sdk: ISdk) {
  tiktokDefaultPlayed = false
  window.onmessage = (
    event: MessageEvent<{
      type: string
      value?: number
      "x-tiktok-player": boolean
    }>
  ) => {
    if (!event.data["x-tiktok-player"]) return
    const frameWindow = event.source as Window

    if (event.data.type === "onPlayerReady") {
      pauseTiktokVideo(frameWindow)

      if (!tiktokDefaultPlayed) {
        tiktokDefaultPlayed = true
        setTimeout(() => playMediaOnLoad(sdk), 300)
      }
    }
  }
}

/**
 * Play media associated with currently active tile
 *
 * @param sdk
 * @param { Element } tile - tile element to check
 * @param { HTMLElement } widgetSelector - the container of the swiper element
 * @param { LookupAttr } lookupAttr - additional attribute lookup options for finding the first slide to load
 * @param { LookupAttr.name } lookupAttr.name - name of the attribute for e.g. data-yt-id or data-tiktok-id
 * @param { LookupAttr.value } lookupAttr.value - required value of the attribute
 */
export function playActiveMediaTileOnLoad(
  sdk: ISdk,
  tile: Element,
  widgetSelector: HTMLElement,
  lookupAttr?: LookupAttr
) {
  if (isActiveTile(sdk, tile, widgetSelector, lookupAttr)) {
    void playMediaOnLoad(sdk)
  }
}

/**
 * Setup onload and onerror events for custom video source
 *
 * @param sdk
 * @param { Element } tile - the tile element
 * @param { HTMLElement } widgetSelector - the container of swiper element
 */
export function setupVideoEvents(sdk: ISdk, tile: Element, widgetSelector: HTMLElement) {
  const videoSourceElement = tile.querySelector<HTMLVideoElement>("video.video-content > source")
  if (videoSourceElement) {
    videoSourceElement.addEventListener("load", () => {
      playActiveMediaTileOnLoad(sdk, tile, widgetSelector)
    })
    videoSourceElement.addEventListener("error", () => {
      videoSourceElement.closest(".video-content-wrapper")?.classList.add("hidden")
      tile.querySelector(".video-fallback-content")?.classList.remove("hidden")
    })
  }
}
